<template>
    <div>
        <dashboard-loader v-if="loading" class="my-7">Loading settings</dashboard-loader>
        <template v-else>
            <v-tabs v-model="tab">
                <v-tab>Application settings</v-tab>
                <v-tab>Add-ons</v-tab>
                <v-tab v-if="nodepoolIsFractional">Share Allocation</v-tab>
            </v-tabs>
            <v-tabs-items v-model="tab">
                <v-tab-item>
                    <div class="pt-4">
                        <v-slider
                            v-model="draft.inactivity_limit"
                            label="Inactivity limit"
                            thumb-label="always"
                            :hint="limitHint"
                            max="24"
                            min="1"
                            class="mt-10 mr-4"
                            persistent-hint
                            :disabled="isTrial"
                            @change="emitInput"></v-slider>
                        <v-checkbox
                            v-model="draft.shared"
                            hint="In shared mode multiple users can connect to the same application, allowing collaborative editing in supported applications."
                            label="Run in shared mode"
                            :disabled="!draft.shared_editable"
                            persistent-hint
                            @change="emitInput"></v-checkbox>
                        <v-checkbox
                            v-model="draft.persistent_home_area"
                            hint="Retain application-specific settings and data in the application's home folder between restarts."
                            label="Persist sessions data in home area between restarts"
                            persistent-hint
                            @change="emitInput"></v-checkbox>
                        <v-select
                            v-model="draft.ipc_mode"
                            class="mt-10"
                            :items="availableIpcOptions"
                            label="Other applications may connect to this app from"
                            hint="Choose which other applications may connect to this application."
                            hide-details
                            outlined
                            dense
                            @change="emitInput"></v-select>
                        <v-text-field
                            v-if="draft.ipc_mode === 'public'"
                            v-model="draft.public_subdomain"
                            class="mt-4"
                            label="Public subdomain"
                            hint="Public internet access for this app (REDCap only). Start with nv-pub-, must be unique across Nuvolos."
                            persistent-hint
                            outlined
                            dense
                            :rules="[publicSubdomainRule]"
                            validate-on-blur
                            @change="emitInput"></v-text-field>
                    </div>
                </v-tab-item>
                <v-tab-item>
                    <v-card flat tile>
                        <v-card-text class="px-1">
                            <v-alert v-if="currentSpaceType === spaceTypes.EDUCATION_SPACE && !isSpaceAdmin" type="info" text>
                                You need to be a
                                <b>space administrator</b>
                                to add or remove add-ons.
                            </v-alert>
                            <v-expansion-panels v-model="expandedAddon">
                                <v-expansion-panel v-for="(addon, index) in draft.addons" :key="addon.name">
                                    <v-expansion-panel-header disable-icon-rotate>
                                        <div>
                                            {{ addon.name }}
                                            <v-icon small>{{ expandedAddon === index ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
                                        </div>
                                        <v-spacer></v-spacer>
                                        <template v-slot:actions>
                                            <v-btn v-if="!addon.editable" text dense color="error" disabled>
                                                {{ addon.enabled ? 'Required' : 'Unavailable' }}
                                            </v-btn>
                                            <v-btn
                                                v-else
                                                text
                                                dense
                                                :color="addon.enabled ? undefined : 'primary'"
                                                :disabled="currentSpaceType === spaceTypes.EDUCATION_SPACE && !isSpaceAdmin"
                                                @click.stop="toggleAddon(addon)">
                                                {{ addon.enabled ? 'Remove' : 'Add' }}
                                            </v-btn>
                                        </template>
                                    </v-expansion-panel-header>
                                    <v-expansion-panel-content>
                                        <v-container>
                                            <div v-if="addon.icon_url" class="float-left mr-3">
                                                <v-img max-width="4em" :src="addon.icon_url" />
                                            </div>
                                            <div class="caption" v-html="addon.description"></div>
                                            <div v-if="addon.doc_link" class="text-right mt-2">
                                                <a :href="addon.doc_link" target="_blank">Go to documentation</a>
                                            </div>
                                        </v-container>
                                    </v-expansion-panel-content>
                                </v-expansion-panel>
                            </v-expansion-panels>
                        </v-card-text>
                    </v-card>
                </v-tab-item>
                <v-tab-item v-if="nodepoolIsFractional">
                    <div class="pt-4">
                        <v-row dense>
                            <v-col v-for="metric in resourceMetrics" :key="metric.label" cols="6">
                                <v-card flat class="grey lighten-3">
                                    <v-card-title class="caption pb-1">
                                        <v-icon small left :color="metric.color">{{ metric.icon }}</v-icon>
                                        {{ metric.label }}
                                    </v-card-title>
                                    <v-card-text class="text-h6">{{ metric.value }}{{ metric.unit }}</v-card-text>
                                </v-card>
                            </v-col>
                        </v-row>
                        <v-slider
                            v-model="draft.hami_resource_limit"
                            label="Allocation"
                            color="primary"
                            track-color="grey lighten-2"
                            thumb-label="always"
                            max="100"
                            min="10"
                            step="10"
                            ticks="always"
                            tick-size="4"
                            class="mt-12 mr-4"
                            persistent-hint
                            hint="Define the share of the physical hardware dedicated to this application."
                            :disabled="isTrial"
                            @change="emitInput">
                            <template v-slot:thumb-label="{ value }">{{ value }}%</template>
                        </v-slider>
                    </div>
                </v-tab-item>
            </v-tabs-items>
        </template>
    </div>
</template>

<script>
import { ipcOptions, isRedcapImage, publicSubdomainRule } from '@/utils'
import { enumsData } from '@/mixins/enums'
import { mapGetters } from 'vuex'

const DashboardLoader = () => import('@/components/DashboardLoader.vue')

export default {
    name: 'ApplicationConfigEditor',
    components: { DashboardLoader },
    mixins: [enumsData],
    props: {
        value: {
            type: Object,
            required: true
        },
        // NC-3133: image family/version the config belongs to; gates the
        // Public access option to REDCap images (see availableIpcOptions).
        image: {
            type: Object,
            default: null
        },
        nodepool: {
            type: Object,
            default: null
        },
        loading: {
            type: Boolean,
            default: false
        },
        isTrial: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            tab: 0,
            expandedAddon: null,
            ipcOptions,
            publicSubdomainRule,
            draft: this.normalize(this.value)
        }
    },
    computed: {
        ...mapGetters('spaceStore', ['isSpaceAdmin', 'currentSpaceType']),
        // NC-3133: Public access only for REDCap images. Kept in the shared
        // ipcOptions list so existing public configs still display.
        availableIpcOptions() {
            if (isRedcapImage(this.image)) return ipcOptions
            return ipcOptions.filter(option => option.value !== 'public')
        },
        nodepoolIsFractional() {
            return Boolean(this.nodepool && (this.nodepool.node_pool?.includes('-hami') || (this.nodepool.cloud === 'switch' && this.nodepool.gpu_type)))
        },
        limitHint() {
            return this.isTrial
                ? 'Cannot change inactivity limit in trial.'
                : 'The number of inactive hours before the app gets shut down automatically (unscaled only).'
        },
        resourceMetrics() {
            const allocation = this.draft.hami_resource_limit / 100
            return [
                { label: 'Processor', icon: 'mdi-memory', color: 'purple', value: this.allocate(this.nodepool?.cpu, allocation), unit: '' },
                { label: 'RAM', icon: 'mdi-memory', color: 'purple', value: this.allocate(this.nodepool?.memory, allocation), unit: ' GB' },
                { label: 'Accelerator', icon: 'mdi-memory mdi-rotate-45', color: 'light-green', value: this.draft.hami_resource_limit, unit: '%' },
                { label: 'HBM', icon: 'mdi-memory mdi-rotate-45', color: 'light-green', value: this.allocate(this.nodepool?.vram, allocation), unit: ' GB' }
            ]
        }
    },
    watch: {
        value: {
            deep: true,
            handler(value) {
                this.draft = this.normalize(value)
            }
        }
    },
    methods: {
        normalize(value) {
            return {
                ...value,
                addons: (value.addons || []).map(addon => ({ ...addon })),
                persistent_home_area: value.persistent_home_area ?? value.home_mount_type === 'ceph_direct',
                hami_resource_limit: value.hami_resource_limit || 100,
                public_subdomain: value.public_subdomain || ''
            }
        },
        allocate(total, allocation) {
            return total ? +(total * allocation).toFixed(1) : 0
        },
        toggleAddon(addon) {
            addon.enabled = !addon.enabled
            this.emitInput()
        },
        emitInput() {
            this.$emit('input', this.normalize(this.draft))
        }
    }
}
</script>
